/*
 * Раскладка плиток звонка: фотоальбом вместо равных клеток.
 *
 * Прежняя сетка (`gridSpec` в callMath.ts) давала одинаковые клетки и вписывала
 * в них кадр целиком, поэтому вертикальное видео висело узкой полоской посреди
 * чёрных полей. Здесь плитка получает форму своего кадра, а строка — общую
 * высоту: получается та же композиция, что у альбома фотографий в Telegram.
 *
 * Функция чистая: ни DOM, ни React, ни времени. Вход — размер области, список
 * участников с пропорциями кадра и прошлая раскладка; выход — координаты.
 *
 * Два правила задают форму (решения Ильи 15.09.2026):
 *   1. Раскладка целиком — прямоугольник: строки одной ширины, края ровные.
 *   2. Растягивать его на всю область не нужно. Он вписывается по большей из
 *      сторон и встаёт по центру.
 *
 * Отсюда обрезка не нужна вовсе: пропорции кадров сохраняются точно, а платой
 * идёт незанятая площадь по краям. Свобода у алгоритма ровно одна — как
 * разбить участников на строки; это и решает, сколько площади останется
 * пустой и насколько разойдутся размеры плиток. Разбиения перебираются все,
 * с сохранением порядка участников, и сравниваются оценкой.
 *
 * Порог `coverThreshold` остаётся для другого случая: кадр сменился (человек
 * повернул телефон), а плитка ещё держит прежнюю форму. Пока расхождение
 * мало, кадр обрезается по краям; дальше вписывается целиком на ровном фоне.
 */

/** Участник глазами раскладки: только имя ключа и пропорция кадра (ширина/высота). */
export type MosaicItem = { id: string; aspect: number };

export type MosaicTile = {
  id: string;
  x: number;
  y: number;
  w: number;
  h: number;
  /** Пропорция самой плитки — она же пропорция кадра после обрезки. */
  aspect: number;
  /** Как вписывать кадр: обрезать по краям или показать целиком на подложке. */
  fit: 'cover' | 'contain';
  /** Номер строки — для отладки и тестов. */
  row: number;
};

export type MosaicLayout = {
  tiles: MosaicTile[];
  /** Сколько плиток в каждой строке: это и есть композиция. */
  rows: number[];
  /** Оценка композиции, больше — лучше. */
  score: number;
  /** Доля площади области, занятая плитками. */
  fill: number;
  /** Во сколько раз пропорция плитки разошлась с пропорцией кадра. */
  crop: number;
};

export type MosaicWeights = {
  /** Награда за занятую площадь — главная цель. */
  fill: number;
  /** Штраф за плитку мельче порога. */
  small: number;
  /** Штраф за разницу площадей между участниками. */
  spread: number;
  /** Штраф за вытянутую плитку. */
  shape: number;
  /** Штраф за одинокую плитку в последней строке. */
  lonely: number;
  /**
   * Штраф за перекос длин строк. Вес крошечный: это не самостоятельное
   * правило, а разрешение ничьих. Без него семь одинаковых кадров вставали
   * как два сверху и пять снизу — просто потому, что такое разбиение
   * попадалось перебору раньше, а балл у него был тот же.
   */
  balance: number;
  /** Штраф за смену композиции — держит раскладку на месте. */
  change: number;
};

export type MosaicConfig = {
  /** Зазор между плитками, он же отступ строк друг от друга. */
  gap: number;
  /**
   * До какого расхождения кадр обрезается (`cover`), а дальше вписывается
   * целиком (`contain`). Считается как отношение пропорций плитки и кадра:
   * при 1.15 из кадра пропадает не больше 13% (1 − 1/1.15). Порог нужен для
   * случая, когда кадр сменился, а плитка ещё держит прежнюю форму: из
   * горизонтальной плитки нельзя вырезать вертикальное видео, не потеряв
   * человека.
   */
  coverThreshold: number;
  /** Ниже этой ширины плитка считается слишком узкой. */
  minTileW: number;
  /** Ниже этой высоты плитка считается слишком низкой. */
  minTileH: number;
  /** Плитка шире этого или уже обратного — вытянутая. */
  maxTileAspect: number;
  /** Больше плиток в строку не ставим. */
  maxPerRow: number;
  /** Насколько новая композиция должна быть лучше прежней, чтобы её сменить. */
  improve: number;
  weights: MosaicWeights;
};

export const MOSAIC_DEFAULTS: MosaicConfig = {
  gap: 6,
  coverThreshold: 1.15,
  minTileW: 96,
  minTileH: 72,
  maxTileAspect: 2.6,
  maxPerRow: 5,
  improve: 0.04,
  weights: { fill: 1, small: 0.5, spread: 0.5, shape: 0.35, lonely: 0.12, balance: 0.01, change: 0.05 },
};

/** Пропорция кадра, пока камера молчит и метаданных ещё нет. */
export const DEFAULT_ASPECT = 16 / 9;

/** Пропорции за этими границами — испорченные данные, а не редкая камера. */
const ASPECT_MIN = 0.3;
const ASPECT_MAX = 3.5;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);

/** Пропорция, которой можно верить: конечное число в разумных границах. */
export function safeAspect(a: number | null | undefined): number {
  if (typeof a !== 'number' || !Number.isFinite(a) || a <= 0) return DEFAULT_ASPECT;
  return clamp(a, ASPECT_MIN, ASPECT_MAX);
}

const compositionCache = new Map<string, number[][]>();

/**
 * Все способы разбить n участников по строкам, не меняя их порядок. Для
 * десяти участников и пяти в строке таких разбиений несколько сотен — перебор
 * дешевле любой эвристики и не зависит от частных случаев.
 */
export function compositions(n: number, maxPerRow: number): number[][] {
  if (n <= 0) return [];
  const key = `${n}:${maxPerRow}`;
  const hit = compositionCache.get(key);
  if (hit) return hit;
  const out: number[][] = [];
  const cur: number[] = [];
  const walk = (left: number) => {
    if (left === 0) {
      out.push([...cur]);
      return;
    }
    for (let p = 1; p <= Math.min(maxPerRow, left); p++) {
      cur.push(p);
      walk(left - p);
      cur.pop();
    }
  };
  walk(n);
  compositionCache.set(key, out);
  return out;
}

type Placed = {
  tiles: MosaicTile[];
  fill: number;
  crop: number;
  minW: number;
  minH: number;
  spread: number;
  shape: number;
  lonely: boolean;
  /** На сколько плиток самая длинная строка длиннее самой короткой. */
  balance: number;
};

/**
 * Геометрия одной композиции.
 *
 * Строки идут одной ширины — раскладка целиком образует прямоугольник, края
 * ровные. Растягивать этот прямоугольник на всю область не обязательно
 * (решение Ильи 15.09.2026): он вписывается по большей из сторон и встаёт по
 * центру. Поэтому обрезать кадры не приходится вовсе — пропорции точные, а
 * платой идёт незанятая площадь по краям.
 *
 * Считается так. Если блок шириной X, высота строки r равна
 * (X − зазоры строки) / Σ(пропорции строки), а высота всего блока линейна по
 * X: H(X) = X·a − b. Отсюда сразу ширина, при которой блок ровно занимает
 * доступную высоту, и берётся меньшее из неё и ширины области.
 */
function place(rows: number[], items: MosaicItem[], box: { w: number; h: number }, cfg: MosaicConfig): Placed | null {
  const { gap } = cfg;
  const k = rows.length;
  const innerH = box.h - gap * (k - 1);
  if (innerH <= 0) return null;

  const sums: number[] = [];
  const gaps: number[] = [];
  let cut = 0;
  for (const m of rows) {
    const slice = items.slice(cut, cut + m);
    cut += m;
    const sum = slice.reduce((s, it) => s + it.aspect, 0);
    if (sum <= 0) return null;
    sums.push(sum);
    gaps.push(gap * (m - 1));
  }

  const a = sums.reduce((s, sum) => s + 1 / sum, 0);
  const b = sums.reduce((s, sum, r) => s + gaps[r] / sum, 0);
  if (!(a > 0)) return null;
  // Ширина блока: либо вся область, либо столько, чтобы блок сошёлся по высоте.
  const blockW = Math.min(box.w, (innerH + b) / a);
  if (!(blockW > 0)) return null;

  const rowH = sums.map((sum, r) => (blockW - gaps[r]) / sum);
  if (rowH.some((h) => !(h > 0))) return null;
  const blockH = rowH.reduce((s, h) => s + h, 0) + gap * (k - 1);
  const x0 = (box.w - blockW) / 2;
  const y0 = (box.h - blockH) / 2;

  const tiles: MosaicTile[] = [];
  let y = y0;
  let i = 0;
  let area = 0;
  let minW = Infinity;
  let minH = Infinity;
  let minArea = Infinity;
  let maxArea = 0;
  let shape = 0;
  for (let r = 0; r < k; r++) {
    const m = rows[r];
    const h = rowH[r];
    let x = x0;
    for (let j = 0; j < m; j++, i++) {
      const it = items[i];
      const w = h * it.aspect;
      tiles.push({ id: it.id, x, y, w, h, aspect: it.aspect, fit: 'cover', row: r });
      x += w + gap;
      area += w * h;
      if (w < minW) minW = w;
      if (h < minH) minH = h;
      if (w * h < minArea) minArea = w * h;
      if (w * h > maxArea) maxArea = w * h;
      const over = Math.max(it.aspect / cfg.maxTileAspect, 1 / (it.aspect * cfg.maxTileAspect), 1);
      if (over > shape) shape = over;
    }
    y += h + gap;
  }
  if (!tiles.length) return null;

  const last = rows[k - 1];
  const widest = Math.max(...rows);

  return {
    tiles,
    fill: area / (box.w * box.h),
    crop: 1,
    minW,
    minH,
    spread: minArea > 0 ? maxArea / minArea : Infinity,
    shape,
    lonely: k > 1 && last === 1 && widest > 1,
    balance: k > 1 ? widest - Math.min(...rows) : 0,
  };
}

/** Балл композиции: занятая площадь минус всё, что портит вид. */
function score(p: Placed, cfg: MosaicConfig, changed: boolean): number {
  const w = cfg.weights;
  const small = Math.max(0, 1 - p.minW / cfg.minTileW) + Math.max(0, 1 - p.minH / cfg.minTileH);
  /*
   * Разброс площадей: до четырёхкратного он неизбежен и не штрафуется —
   * столько даёт «одна плитка сверху против ряда из двух», а это нормальный
   * альбом. Дальше начинается перекос: ряд из трёх под одной большой плиткой
   * мельчит троих ради одного (замечание Ильи 15.09.2026).
   */
  const spread = Math.max(0, Math.log2(p.spread || 1) - 2);
  return (
    p.fill * w.fill -
    small * w.small -
    spread * w.spread -
    (p.shape - 1) * w.shape -
    (p.lonely ? w.lonely : 0) -
    p.balance * w.balance -
    (changed ? w.change : 0)
  );
}

const sameRows = (a: number[], b: number[]) => a.length === b.length && a.every((v, i) => v === b[i]);

export type MosaicInput = {
  /** Область под плитки: отступы вокруг вычитает вызывающий. */
  container: { w: number; h: number };
  items: MosaicItem[];
  config?: Partial<MosaicConfig> & { weights?: Partial<MosaicWeights> };
  /** Прошлая раскладка: её композиция меняется только при заметном выигрыше. */
  prev?: MosaicLayout | null;
};

const EMPTY: MosaicLayout = { tiles: [], rows: [], score: 0, fill: 0, crop: 1 };

/**
 * Разложить участников по области. Перебирает разбиения на строки с
 * сохранением порядка, оценивает каждое и берёт лучшее. Прошлая композиция
 * получает фору `improve`: иначе сетка перещёлкивалась бы между двумя видами
 * от смещения окна на пиксель.
 */
export function planMosaic(input: MosaicInput): MosaicLayout {
  const cfg: MosaicConfig = {
    ...MOSAIC_DEFAULTS,
    ...input.config,
    weights: { ...MOSAIC_DEFAULTS.weights, ...input.config?.weights },
  };
  const items = input.items.map((it) => ({ id: it.id, aspect: safeAspect(it.aspect) }));
  const box = input.container;
  if (!items.length || !(box.w > 0) || !(box.h > 0)) return EMPTY;

  const prevRows = input.prev?.rows ?? null;
  let best: { layout: MosaicLayout; raw: number } | null = null;
  let keep: { layout: MosaicLayout; raw: number } | null = null;

  for (const rows of compositions(items.length, cfg.maxPerRow)) {
    const placed = place(rows, items, box, cfg);
    if (!placed) continue;
    const held = !!prevRows && sameRows(rows, prevRows);
    const raw = score(placed, cfg, false);
    const layout: MosaicLayout = {
      tiles: placed.tiles,
      rows: [...rows],
      score: raw,
      fill: placed.fill,
      crop: placed.crop,
    };
    if (held) keep = { layout, raw };
    if (!best || raw > best.raw) best = { layout, raw };
  }

  if (!best) return EMPTY;
  // Прежняя композиция остаётся, пока новая не лучше на порог улучшения.
  if (keep && best.raw - keep.raw < cfg.improve) return keep.layout;
  return best.layout;
}
